<script setup>
import { ref, computed } from 'vue'
import Papa from 'papaparse'
import AccessCatalogKeysGuide from './AccessCatalogKeysGuide.vue'
import { UserAccessBulkService } from '../../services/UserAccessBulkService'
import { RequestService } from '../../services/RequestService'
import {
  parseCommaSeparatedKeys,
  catalogKeySet,
  findUnknownKeys,
  unknownKeysMessage,
  downloadCsv
} from '../../utils/accessCsv'
import {
  AGENCY_USER_ACCESS_FORBIDDEN_PERMISSION_KEYS
} from '../../access/permissionKeys'
import {
  AGENCY_USER_ACCESS_FORBIDDEN_ROLE_KEYS
} from '../../access/roleKeys'

const props = defineProps({
  catalog: { type: Object, default: null }
})

const { handleErrors } = RequestService()
const bulkService = UserAccessBulkService()

const csvFile = ref(null)
const csvInput = ref(null)
const loading = ref(false)
const step = ref('upload')
const errors = ref([])
const previewUsers = ref([])
const strategies = ref({})
const processedUsers = ref([])

const catalogRoleKeys = computed(() => catalogKeySet(props.catalog?.roles))
const catalogPermissionKeys = computed(() => catalogKeySet(props.catalog?.permissions))
const forbiddenRoles = new Set(AGENCY_USER_ACCESS_FORBIDDEN_ROLE_KEYS)
const forbiddenPermissions = new Set(AGENCY_USER_ACCESS_FORBIDDEN_PERMISSION_KEYS)

const conflictUsers = computed(() => previewUsers.value.filter((user) => user.hasConflict))
const allConflictsResolved = computed(() =>
  conflictUsers.value.every((user) => (
    strategies.value[user.email] === 'replace' || strategies.value[user.email] === 'combine'
  ))
)
const canApply = computed(() => (
  previewUsers.value.length > 0 && allConflictsResolved.value && !loading.value
))

function validateAccessKeys(roleKeys, permissionGrants, permissionDenials) {
  const errs = []
  const unknownRoles = findUnknownKeys(roleKeys, catalogRoleKeys.value)
  const unknownGrants = findUnknownKeys(permissionGrants, catalogPermissionKeys.value)
  const unknownDenials = findUnknownKeys(permissionDenials, catalogPermissionKeys.value)
  const roleMsg = unknownKeysMessage(unknownRoles, 'role key(s)')
  const grantMsg = unknownKeysMessage(unknownGrants, 'permission grant(s)')
  const denialMsg = unknownKeysMessage(unknownDenials, 'permission denial(s)')
  if (roleMsg) errs.push(roleMsg)
  if (grantMsg) errs.push(grantMsg)
  if (denialMsg) errs.push(denialMsg)

  const blockedRoles = roleKeys.filter((key) => forbiddenRoles.has(key))
  const blockedPermissions = [...permissionGrants, ...permissionDenials]
    .filter((key) => forbiddenPermissions.has(key))
  if (blockedRoles.length) {
    errs.push(`Role key(s) cannot be assigned from bulk user access: ${blockedRoles.join(', ')}`)
  }
  if (blockedPermissions.length) {
    errs.push(`Permission key(s) cannot be assigned from bulk user access: ${[...new Set(blockedPermissions)].join(', ')}`)
  }
  return errs
}

function parseUpdateCsv(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        const validUsers = []
        const invalidUsers = []
        const counts = {}
        data.forEach((row) => {
          const email = row.Email?.toLowerCase().trim()
          if (email) counts[email] = (counts[email] || 0) + 1
        })
        data.forEach((row) => {
          if (!Object.values(row).some((value) => value?.trim())) return
          const email = row.Email?.toLowerCase().trim() || ''
          const roleKeys = parseCommaSeparatedKeys(row.RoleKeys)
          const permissionGrants = parseCommaSeparatedKeys(row.PermissionGrants)
          const permissionDenials = parseCommaSeparatedKeys(row.PermissionDenials)
          const errs = []
          if (!email) errs.push('Email is required')
          if (email && counts[email] > 1) errs.push('Duplicate email')
          errs.push(...validateAccessKeys(roleKeys, permissionGrants, permissionDenials))
          const normalized = { email, roleKeys, permissionGrants, permissionDenials }
          if (errs.length) invalidUsers.push({ ...normalized, errors: errs.join(', ') })
          else validUsers.push(normalized)
        })
        resolve({ validUsers, invalidUsers })
      },
      error: (err) => reject(err)
    })
  })
}

function resetResults() {
  errors.value = []
  processedUsers.value = []
}

function handleFileUpload(event) {
  const file = event.target.files[0]
  csvFile.value = file || null
  resetResults()
  step.value = 'upload'
  previewUsers.value = []
  strategies.value = {}
}

function downloadTemplate() {
  downloadCsv('user_access_update_template.csv', [
    'Email,RoleKeys,PermissionGrants,PermissionDenials',
    'existing.user@example.com,agencyAdmin,transform.access,'
  ])
}

function extrasLabel(extras = {}) {
  const parts = []
  if (extras.roleKeys?.length) parts.push(`roles: ${extras.roleKeys.join(', ')}`)
  if (extras.permissionGrants?.length) parts.push(`grants: ${extras.permissionGrants.join(', ')}`)
  if (extras.permissionDenials?.length) parts.push(`denials: ${extras.permissionDenials.join(', ')}`)
  return parts.join(' · ') || '—'
}

function setAllStrategies(strategy) {
  const next = { ...strategies.value }
  conflictUsers.value.forEach((user) => {
    next[user.email] = strategy
  })
  strategies.value = next
}

function setStrategy(email, strategy) {
  strategies.value = { ...strategies.value, [email]: strategy }
}

function pushDetailErrors(details) {
  const list = details?.errors || []
  list.forEach((item) => {
    errors.value.push({ email: item.email || 'no email', error: item.error })
  })
  const conflicts = details?.conflicts || []
  conflicts.forEach((item) => {
    errors.value.push({
      email: item.email || 'no email',
      error: `Unresolved conflict (${extrasLabel(item.extras)})`
    })
  })
}

async function previewUpdates() {
  if (!csvFile.value) {
    handleErrors('Error previewing user access: ', new Error('Please select a CSV file'))
    return
  }
  loading.value = true
  resetResults()
  previewUsers.value = []
  strategies.value = {}
  try {
    const { validUsers, invalidUsers } = await parseUpdateCsv(csvFile.value)
    invalidUsers.forEach((user) => {
      errors.value.push({ email: user.email || 'no email', error: user.errors })
    })
    if (invalidUsers.length || !validUsers.length) {
      if (!validUsers.length && !invalidUsers.length) {
        errors.value.push({ email: 'CSV', error: 'No user rows found' })
      }
      return
    }
    const data = await bulkService.preview(validUsers)
    previewUsers.value = data?.users || []
    step.value = 'review'
  } catch (error) {
    pushDetailErrors(error.details)
    if (!errors.value.length) {
      errors.value.push({ email: 'System Error', error: error.message })
    }
    handleErrors('BulkUserAccessUpload.preview', error)
  } finally {
    loading.value = false
  }
}

async function applyUpdates() {
  if (!canApply.value) return
  loading.value = true
  resetResults()
  try {
    const payload = previewUsers.value.map((user) => ({
      email: user.email,
      roleKeys: user.incoming.roleKeys,
      permissionGrants: user.incoming.permissionGrants,
      permissionDenials: user.incoming.permissionDenials,
      ...(user.hasConflict ? { conflictStrategy: strategies.value[user.email] } : {})
    }))
    const data = await bulkService.apply(payload)
    processedUsers.value = (data?.users || []).map((user) => ({
      email: user.email,
      status: 'Access updated'
    }))
    step.value = 'done'
    csvFile.value = null
    if (csvInput.value) csvInput.value.value = ''
  } catch (error) {
    pushDetailErrors(error.details)
    if (!errors.value.length) {
      errors.value.push({ email: 'System Error', error: error.message })
    }
    handleErrors('BulkUserAccessUpload.apply', error)
  } finally {
    loading.value = false
  }
}

function startOver() {
  step.value = 'upload'
  csvFile.value = null
  previewUsers.value = []
  strategies.value = {}
  resetResults()
  if (csvInput.value) csvInput.value.value = ''
}
</script>

<template>
  <div>
    <div class="mb-4">
      <p>
        Upload a CSV to update existing users in any agency. Columns:
        <strong>Email, RoleKeys, PermissionGrants, PermissionDenials</strong>
      </p>
      <p class="text-muted mb-0">
        Empty cells mean an empty list for that field. If a user already has keys that are not in the CSV,
        you must choose replace or combine before anything is saved. Invalid keys or missing users block the whole file.
      </p>
    </div>

    <AccessCatalogKeysGuide
      :roles="catalog?.roles || []"
      :permissions="catalog?.permissions || []"
    />

    <div v-if="step === 'upload' || step === 'done'" class="mb-2">
      <button class="btn btn-outline-secondary" type="button" @click="downloadTemplate">
        Download CSV Template
      </button>
    </div>
    <div v-if="step === 'upload' || step === 'done'" class="mb-4">
      <input
        ref="csvInput"
        type="file"
        class="form-control"
        accept=".csv"
        :disabled="loading"
        @change="handleFileUpload"
      >
    </div>
    <button
      v-if="step === 'upload' || step === 'done'"
      class="btn btn-primary"
      type="button"
      :disabled="!csvFile || loading"
      @click="previewUpdates"
    >
      <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" />
      {{ loading ? 'Checking...' : 'Preview updates' }}
    </button>

    <div v-if="step === 'review'" class="mt-2">
      <p class="mb-2">
        {{ previewUsers.length }} user(s) ready.
        <span v-if="conflictUsers.length">
          {{ conflictUsers.length }} have existing keys not listed in the CSV.
        </span>
        <span v-else>No conflicts. Confirm to apply all updates.</span>
      </p>

      <div v-if="conflictUsers.length" class="mb-3 d-flex gap-2 flex-wrap">
        <button class="btn btn-sm btn-outline-secondary" type="button" @click="setAllStrategies('replace')">
          Replace all
        </button>
        <button class="btn btn-sm btn-outline-secondary" type="button" @click="setAllStrategies('combine')">
          Combine all
        </button>
      </div>

      <div class="table-responsive mb-3">
        <table class="table table-sm align-middle">
          <thead>
            <tr>
              <th>Email</th>
              <th>Agency</th>
              <th>Incoming</th>
              <th>Existing extras</th>
              <th v-if="conflictUsers.length">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in previewUsers" :key="user.email">
              <td>
                <div>{{ user.email }}</div>
                <div v-if="user.fullname" class="small text-muted">{{ user.fullname }}</div>
              </td>
              <td>{{ user.agencyId || '—' }}</td>
              <td class="small">
                <div v-if="user.incoming.roleKeys.length">roles: {{ user.incoming.roleKeys.join(', ') }}</div>
                <div v-if="user.incoming.permissionGrants.length">grants: {{ user.incoming.permissionGrants.join(', ') }}</div>
                <div v-if="user.incoming.permissionDenials.length">denials: {{ user.incoming.permissionDenials.join(', ') }}</div>
                <div
                  v-if="!user.incoming.roleKeys.length && !user.incoming.permissionGrants.length && !user.incoming.permissionDenials.length"
                  class="text-muted"
                >
                  (empty access)
                </div>
              </td>
              <td class="small">
                <span v-if="user.hasConflict">{{ extrasLabel(user.extras) }}</span>
                <span v-else class="text-muted">None</span>
              </td>
              <td v-if="conflictUsers.length">
                <div v-if="user.hasConflict" class="d-flex flex-column gap-1">
                  <label class="small mb-0">
                    <input
                      class="form-check-input me-1"
                      type="radio"
                      :name="`strategy-${user.email}`"
                      :checked="strategies[user.email] === 'replace'"
                      @change="setStrategy(user.email, 'replace')"
                    >
                    Replace
                  </label>
                  <label class="small mb-0">
                    <input
                      class="form-check-input me-1"
                      type="radio"
                      :name="`strategy-${user.email}`"
                      :checked="strategies[user.email] === 'combine'"
                      @change="setStrategy(user.email, 'combine')"
                    >
                    Combine
                  </label>
                </div>
                <span v-else class="text-muted small">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" type="button" :disabled="loading" @click="startOver">
          Cancel
        </button>
        <button class="btn btn-primary" type="button" :disabled="!canApply" @click="applyUpdates">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" />
          {{ loading ? 'Applying...' : 'Apply all updates' }}
        </button>
      </div>
      <p v-if="conflictUsers.length && !allConflictsResolved" class="text-muted small mt-2 mb-0">
        Choose replace or combine for every conflicted user before applying. Nothing will be saved until then.
      </p>
    </div>

    <div v-if="processedUsers.length" class="mt-3">
      <h6>Successfully updated users:</h6>
      <ul class="list-group">
        <li
          v-for="user in processedUsers"
          :key="user.email"
          class="list-group-item list-group-item-success"
        >
          {{ user.email }} - {{ user.status }}
        </li>
      </ul>
    </div>

    <div v-if="errors.length" class="mt-3">
      <h6>Errors:</h6>
      <ul class="list-group">
        <li
          v-for="error in errors"
          :key="`${error.email}-${error.error}`"
          class="list-group-item list-group-item-danger"
        >
          {{ error.email }} - {{ error.error }}
        </li>
      </ul>
    </div>
  </div>
</template>
